// Purchases via RevenueCat (@revenuecat/purchases-capacitor).
//
// The device never decides entitlement. RevenueCat validates the receipt with
// Apple and tells our server over the webhook at /api/subscription/rc-webhook,
// which is the only writer of subscription rows. The client's CustomerInfo is
// used for responsiveness (unlock immediately after a purchase), never as the
// source of truth — /api/entitlement stays authoritative.
import {
  Purchases,
  PACKAGE_TYPE,
  PURCHASES_ERROR_CODE,
  LOG_LEVEL,
  type PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { registerPlugin } from "@capacitor/core";
import { isNativeApp } from "./native";

interface SubscriptionsPlugin {
  showManageSubscriptions(): Promise<void>;
  /** StoreKit 2 directly — the same API the purchase sheet renders from. */
  getLocalizedPrices(options: { productIds: string[] }): Promise<{
    products: { identifier: string; priceString: string; price: number }[];
    storefront: string;
  }>;
}
const CalolySubscriptions = registerPlugin<SubscriptionsPlugin>("CalolySubscriptions");
import { invalidateEntitlement, pollEntitlement } from "./useEntitlement";

/** RevenueCat entitlement identifier — matches the dashboard and our tiers. */
export const PREMIUM_ENTITLEMENT = "premium";

export const PRODUCT_IDS = {
  yearly: "caloly_premium_yearly",
  monthly: "caloly_premium_monthly",
} as const;

export interface OfferingPrice {
  identifier: string;
  priceString: string;
  /** Numeric price in the store's currency — the savings maths needs a number,
   *  and parsing it back out of a localized string is a bug waiting to happen
   *  (currency symbols, comma decimals, non-breaking spaces). */
  price: number;
  period: "monthly" | "yearly";
}

/** Prices plus their provenance. The provenance is rendered on the paywall in
 *  small type — after a round of "the app says $ but Apple says €" the screen
 *  itself now states which store answered and which storefront it believes,
 *  so a single screenshot carries its own diagnosis. */
export interface PriceInfo {
  prices: OfferingPrice[] | null;
  source: "storekit" | "revenuecat" | "static";
  /** App Store storefront country code (e.g. "DEU"); only known on the
   *  StoreKit-direct path. */
  storefront: string | null;
}

/** Public SDK key (appl_…). Absent until the App Store app exists in
 *  RevenueCat, so every entry point below degrades instead of throwing. */
const API_KEY = import.meta.env.VITE_RC_APPLE_API_KEY as string | undefined;

/** RevenueCat is usable only in the native shell with a key configured. */
export function purchasesAvailable(): boolean {
  return isNativeApp() && Boolean(API_KEY);
}

let configured = false;
// Resolves when configurePurchases() has finished its first run, so
// getPriceInfo() never queries products before the SDK is ready — an early
// empty fetch is exactly what dropped the paywall to its hardcoded fallback.
let resolveReady: () => void;
const readyPromise = new Promise<void>((r) => { resolveReady = r; });
export function configureReady(): Promise<void> { return readyPromise; }

/** Configure once, then bind the store account to our user id so entitlements
 *  follow the caloly account rather than the device. */
export async function configurePurchases(userId: number): Promise<void> {
  if (!purchasesAvailable()) return;
  try {
    if (!configured) {
      if (import.meta.env.DEV) await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      await Purchases.configure({ apiKey: API_KEY!, appUserID: String(userId) });
      configured = true;
      // Renewals, refunds and cancellations arrive here without a purchase.
      // The webhook has already told the server; just drop the cached tier.
      await Purchases.addCustomerInfoUpdateListener(() => invalidateEntitlement());
    } else {
      await Purchases.logIn({ appUserID: String(userId) });
      invalidateEntitlement();
    }
    resolveReady();
  } catch (e) {
    resolveReady();
    // Offline or misconfigured: the server-side entitlement still governs, so
    // a failure here costs responsiveness, not access.
    console.warn("[purchases] configure failed", e);
  }
}

export async function logOutPurchases(): Promise<void> {
  if (!purchasesAvailable() || !configured) return;
  try {
    // Back to an anonymous id so the next account doesn't inherit this one.
    await Purchases.logOut();
  } catch { /* signing out locally must never be blocked by the store */ }
}

function periodOf(pkg: PurchasesPackage): "monthly" | "yearly" | null {
  if (pkg.packageType === PACKAGE_TYPE.ANNUAL) return "yearly";
  if (pkg.packageType === PACKAGE_TYPE.MONTHLY) return "monthly";
  // Custom packages carry no package type; fall back to the product id.
  if (pkg.product.identifier === PRODUCT_IDS.yearly) return "yearly";
  if (pkg.product.identifier === PRODUCT_IDS.monthly) return "monthly";
  return null;
}

async function currentPackages(): Promise<PurchasesPackage[]> {
  const { current } = await Purchases.getOfferings();
  return current?.availablePackages ?? [];
}

/** Store-localized prices for caloly's own paywall. App Review requires the
 *  real price and period to be shown before purchase.
 *
 *  Source order is a trust order, and it exists because a German paywall once
 *  advertised $34.99 while Apple's sheet charged 39,99 €:
 *    1. StoreKit 2 via our own native plugin — zero intermediaries, the same
 *       API the sheet renders from, plus the storefront country for the logs.
 *    2. The RevenueCat SDK's getProducts — same store, one SDK in between.
 *    3. null → the UI's static € prices. NEVER the RC offering metadata; its
 *       backend reference prices are what lied in the first place.
 *  Apple prices by the signed-in account's App Store storefront — not device
 *  locale, not location — so asking the store is the only correct "where is
 *  the user from". */
export async function getPriceInfo(): Promise<PriceInfo> {
  if (!purchasesAvailable()) return { prices: null, source: "static", storefront: null };
  // Never read prices before the SDK has configured — an empty read here is
  // what used to fall through to a hardcoded € on the button.
  await Promise.race([configureReady(), new Promise<void>((r) => setTimeout(r, 4000))]);

  const toPeriod = (id: string): "monthly" | "yearly" | null =>
    id === PRODUCT_IDS.yearly ? "yearly" : id === PRODUCT_IDS.monthly ? "monthly" : null;

  // 1) The offering package's StoreKit product — the EXACT object purchase()
  //    charges via purchasePackage. Its priceString is Apple's own localized
  //    price for the signed-in account's storefront (NOT the RC dashboard
  //    reference price), so the button and the sheet read the same number.
  try {
    const pkgs = await currentPackages();
    const fromPkgs = pkgs
      .map((pkg) => {
        const period = periodOf(pkg);
        const p = pkg.product;
        return period && p?.priceString
          ? { identifier: p.identifier, priceString: p.priceString, price: p.price, period }
          : null;
      })
      .filter((p): p is OfferingPrice => p !== null);
    if (fromPkgs.length) {
      console.warn("[purchases] price source: offering package (charged product)");
      return { prices: fromPkgs, source: "revenuecat", storefront: null };
    }
  } catch (e) {
    console.warn("[purchases] offering packages failed", e);
  }

  // 2) getProducts — the SAME fetch purchase() falls back to
  //    (purchaseStoreProduct), so still exact parity with the charge.
  try {
    const { products } = await Purchases.getProducts({
      productIdentifiers: [PRODUCT_IDS.yearly, PRODUCT_IDS.monthly],
    });
    const viaSdk = products
      .map((p) => {
        const period = toPeriod(p.identifier);
        return period ? { identifier: p.identifier, priceString: p.priceString, price: p.price, period } : null;
      })
      .filter((p): p is OfferingPrice => p !== null);
    if (viaSdk.length) {
      console.warn("[purchases] price source: revenuecat getProducts");
      return { prices: viaSdk, source: "revenuecat", storefront: null };
    }
  } catch (e) {
    console.warn("[purchases] getProducts failed", e);
  }

  // 3) StoreKit 2 through our own plugin — corroboration + the storefront code
  //    for diagnosis. Some setups return empty here even when RC does not.
  try {
    const { products, storefront } = await CalolySubscriptions.getLocalizedPrices({
      productIds: [PRODUCT_IDS.yearly, PRODUCT_IDS.monthly],
    });
    const direct = products
      .map((p) => {
        const period = toPeriod(p.identifier);
        return period ? { identifier: p.identifier, priceString: p.priceString, price: p.price, period } : null;
      })
      .filter((p): p is OfferingPrice => p !== null);
    if (direct.length) {
      console.warn(`[purchases] price source: storekit-direct (storefront ${storefront})`);
      return { prices: direct, source: "storekit", storefront };
    }
    console.warn(`[purchases] storekit-direct empty (storefront ${storefront})`);
  } catch (e) {
    console.warn("[purchases] storekit-direct failed", e);
  }

  // 4) Nothing fetchable. Return null prices — the UI must show "price at
  //    checkout", NEVER a hardcoded currency that could contradict the charge.
  console.warn("[purchases] no live price — UI will defer to the checkout sheet");
  return { prices: null, source: "static", storefront: null };
}

export type PurchaseResult = "premium" | "pending" | "cancelled" | "failed";

export async function purchase(period: "monthly" | "yearly"): Promise<PurchaseResult> {
  if (!purchasesAvailable()) return "failed";
  try {
    // Preferred: the offering package (feeds RC's analytics and A/B tooling).
    // But an offering is dashboard configuration — a package can be missing or
    // point at the wrong store's products — and StoreKit itself is not. If the
    // offering has nothing usable, buy the store product directly; the receipt
    // reaches RevenueCat and the webhook either way.
    const pkg = (await currentPackages().catch(() => [])).find((p) => periodOf(p) === period);
    let customerInfo;
    if (pkg) {
      ({ customerInfo } = await Purchases.purchasePackage({ aPackage: pkg }));
    } else {
      console.warn("[purchases] no offering package for", period, "— buying store product directly");
      const { products } = await Purchases.getProducts({ productIdentifiers: [PRODUCT_IDS[period]] });
      const product = products.find((p) => p.identifier === PRODUCT_IDS[period]);
      if (!product) return "failed";
      ({ customerInfo } = await Purchases.purchaseStoreProduct({ product }));
    }
    invalidateEntitlement();
    if (!customerInfo.entitlements.active[PREMIUM_ENTITLEMENT]) return "pending";

    // RevenueCat already knows; our server learns via the webhook, which
    // usually lands in seconds. Poll so the UI unlocks without a restart.
    const server = await pollEntitlement();
    return server.tier === "premium" ? "premium" : "pending";
  } catch (e) {
    const err = e as { code?: string; userCancelled?: boolean | null };
    if (err.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR || err.userCancelled) {
      return "cancelled";
    }
    if (err.code === PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR) return "pending";
    console.warn("[purchases] purchase failed", e);
    return "failed";
  }
}

/** App Review requires an explicit restore path on every paywall. */
export async function restorePurchases(): Promise<"premium" | "none" | "failed"> {
  if (!purchasesAvailable()) return "failed";
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    invalidateEntitlement();
    if (!customerInfo.entitlements.active[PREMIUM_ENTITLEMENT]) return "none";
    const server = await pollEntitlement();
    return server.tier === "premium" ? "premium" : "none";
  } catch {
    return "failed";
  }
}

/**
 * Open Apple's subscription-management sheet over the app.
 *
 * Apple provides no way for an app to cancel a subscription itself — that is
 * deliberate, so an app can never end a subscription without the user seeing
 * Apple's own confirmation. This sheet is the closest thing: the user cancels
 * or switches plan without leaving caloly.
 *
 * Returns false when the native sheet isn't available (web, older iOS, no
 * window scene) so the caller can fall back to the App Store URL.
 */
export async function openManageSubscriptions(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    await CalolySubscriptions.showManageSubscriptions();
    return true;
  } catch {
    return false;
  }
}
